/* ══════════════════════════════════════════════════════════════════════════════
   GW ANALYTICS — thin custom-event layer                        (added 2026-08)

   WHY THIS EXISTS
   The theme had no analytics abstraction at all: nothing pushed to a data layer,
   nothing emitted DOM events. Every section that wanted to report an interaction
   would have had to invent its own transport, so this file is the one transport.

   WHAT IT DELIBERATELY DOES NOT DO
   Shopify's own Web Pixels already emit the standard commerce events
   (page_viewed, product_viewed, product_added_to_cart, checkout_started...).
   Re-emitting those here would double-count every funnel step in GA4/GTM. So:

     • `add_to_cart` below is a *merchandising-context* event. It carries which
       flavor / pack / purchase mode / source section produced the add, which the
       Shopify pixel cannot see. It is namespaced `gw_` on the data layer exactly
       so it can never be mistaken for the platform's own add-to-cart.
     • Nothing here fires on page load except the IntersectionObserver "view"
       events, which have no platform equivalent.

   TRANSPORT
     1. window.dataLayer  — GTM/GA4 pick it up. Event name is `gw_<name>`.
     2. document CustomEvent `gw:analytics` — for anything else that wants to listen.
   Both carry the same payload. If neither consumer exists the calls are harmless.

   FIRE-ONCE RULES (release blocker 14: "Analytics fire once per action")
     • `view_*` events fire at most once per page load, latched by event name.
     • Everything else is deduped on (name + payload) within a 400ms window, which
       kills the double-fire you get from a <label>-wrapped radio (the click
       bubbles from both the label and the input) without suppressing a genuine
       repeat action by the same customer a moment later.

   PRIVACY
   `sanitize()` drops any key that could carry personal data and any value that
   looks like an email address, so a copy-paste mistake in a section cannot leak
   a customer identifier into the data layer.
   ══════════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var BLOCKED_KEYS = /(^|_)(email|mail|name|phone|address|customer|user|first|last|zip|postal|ip)($|_)/i;
  var EMAILISH = /[^\s@]+@[^\s@]+\.[^\s@]+/;

  var viewLatch = Object.create(null);
  var recent = Object.create(null);

  function sanitize(payload) {
    var out = {};
    if (!payload || typeof payload !== 'object') return out;
    Object.keys(payload).forEach(function (k) {
      var v = payload[k];
      if (v === null || v === undefined || v === '') return;
      if (BLOCKED_KEYS.test(k)) return;
      if (typeof v === 'string' && EMAILISH.test(v)) return;
      if (typeof v === 'object') return;
      out[k] = v;
    });
    return out;
  }

  /* Stable key for the dedup window. Object.keys order is insertion order, so
     sort it — otherwise two logically identical payloads built in a different
     order would look like different events and both would fire. */
  function signature(name, data) {
    var keys = Object.keys(data).sort();
    var parts = [name];
    for (var i = 0; i < keys.length; i++) parts.push(keys[i] + '=' + data[keys[i]]);
    return parts.join('|');
  }

  function track(name, payload) {
    if (!name) return;
    var data = sanitize(payload);

    if (name.indexOf('view_') === 0) {
      if (viewLatch[name]) return;
      viewLatch[name] = true;
    } else {
      var sig = signature(name, data);
      var now = (window.performance && performance.now) ? performance.now() : Date.now();
      if (recent[sig] && (now - recent[sig]) < 400) return;
      recent[sig] = now;
    }

    window.dataLayer = window.dataLayer || [];
    var entry = { event: 'gw_' + name };
    Object.keys(data).forEach(function (k) { entry[k] = data[k]; });
    window.dataLayer.push(entry);

    try {
      document.dispatchEvent(new CustomEvent('gw:analytics', {
        detail: { name: name, payload: data }
      }));
    } catch (e) { /* CustomEvent constructor unavailable — data layer already has it */ }
  }

  /* Fire `name` the first time `el` is at least 40% visible. Falls back to firing
     immediately where IntersectionObserver is missing, because a missing view
     event is worse than an early one for a section that is on the page anyway. */
  function trackView(el, name, payload) {
    if (!el || !name) return;
    if (!('IntersectionObserver' in window)) { track(name, payload); return; }
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          track(name, payload);
          io.disconnect();
          return;
        }
      }
    }, { threshold: 0.4 });
    io.observe(el);
  }

  /* ── Declarative binding ────────────────────────────────────────────────────
     Sections opt in with attributes instead of inline script:

       data-gw-track="click_cross_sell"   data-gw-<key>="value"   → on click
       data-gw-view="view_squares_launch" data-gw-<key>="value"   → on 40% visible

     Payload keys come from every data-gw-* attribute except `track` and `view`,
     so `data-gw-product="squares"` becomes `{product: "squares"}`. Delegated at
     the document level, so sections rendered later still work. */
  function payloadFrom(el) {
    var out = {};
    if (!el.dataset) return out;
    Object.keys(el.dataset).forEach(function (k) {
      if (!/^gw[A-Z]/.test(k)) return;
      var key = k.slice(2);
      key = key.charAt(0).toLowerCase() + key.slice(1);
      key = key.replace(/[A-Z]/g, function (c) { return '_' + c.toLowerCase(); });
      if (key === 'track' || key === 'view') return;
      out[key] = el.dataset[k];
    });
    return out;
  }

  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest ? e.target.closest('[data-gw-track]') : null;
    if (!el) return;
    track(el.dataset.gwTrack, payloadFrom(el));
  }, true);

  function bindViews(root) {
    (root || document).querySelectorAll('[data-gw-view]').forEach(function (el) {
      if (el.__gwViewBound) return;
      el.__gwViewBound = true;
      trackView(el, el.dataset.gwView, payloadFrom(el));
    });
  }

  window.gwAnalytics = { track: track, trackView: trackView, bindViews: bindViews };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bindViews(); });
  } else {
    bindViews();
  }
  document.addEventListener('shopify:section:load', function (e) { bindViews(e.target); });
})();
